import { memo } from 'react';
import { LoadingSpinner } from './LoadingSpinner';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';

export type LoadingType = 'spinner' | 'skeleton' | 'card-grid' | 'table' | 'minimal';

interface PageLoaderProps {
  type?: LoadingType;
  message?: string;
  className?: string;
  count?: number;
  height?: string;
  fullScreen?: boolean;
}

const PageLoader = memo<PageLoaderProps>(({
  type = 'spinner',
  message = 'Loading...',
  className,
  count = 4,
  height = 'h-24',
  fullScreen = false
}) => {
  const containerClass = cn(
    'w-full',
    fullScreen && 'min-h-screen flex items-center justify-center',
    className
  );

  switch (type) {
    case 'spinner':
      return (
        <div className={containerClass}>
          <LoadingSpinner message={message} fullScreen={fullScreen} />
        </div>
      );

    case 'skeleton':
      return (
        <div className={cn('space-y-4', containerClass)}>
          <Skeleton className={cn('w-full', height)} />
          <div className="grid gap-4">
            {Array.from({ length: count }).map((_, i) => (
              <Skeleton key={i} className={cn('w-full', height)} />
            ))}
          </div>
        </div>
      );

    case 'card-grid':
      return (
        <div className={containerClass}>
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
            {Array.from({ length: count }).map((_, i) => (
              <div key={i} className={cn(height, 'bg-muted/30 rounded-lg animate-pulse')} />
            ))}
          </div>
        </div>
      );

    case 'table':
      return (
        <div className={cn('space-y-4', containerClass)}>
          {/* Table header skeleton */}
          <Skeleton className="h-12 w-full" />
          {/* Table rows skeleton */}
          <div className="space-y-2">
            {Array.from({ length: count }).map((_, i) => (
              <Skeleton key={i} className="h-16 w-full" />
            ))}
          </div>
        </div>
      );

    case 'minimal':
      return (
        <div className={cn('flex items-center justify-center py-8', containerClass)}>
          <div className="flex items-center space-x-2">
            <div className="w-4 h-4 border-2 border-t-transparent border-primary rounded-full animate-spin" />
            <span className="text-muted-foreground text-sm">{message}</span>
          </div>
        </div>
      );

    default:
      return (
        <div className={containerClass}>
          <LoadingSpinner message={message} fullScreen={fullScreen} />
        </div>
      );
  }
});

PageLoader.displayName = 'PageLoader';

export default PageLoader;